import { pick } from 'ramda';
import { useMemo } from 'react';
import { dispatcher } from 'dispatcher';
import { useAppSelector } from 'js/store/hooks';

import * as hooks from './hooks';

export const useMenuPublicAPI = () => {
    const { menuItems, rootPage } = useAppSelector(pick(['menuItems', 'rootPage']));
    const getMenuConfigAsync = hooks.useGetMenuConfigAsync();

    return useMemo(() => ({
        /**
         * Get configured root page.
         * @memberOf Playtech.API.menu
         * @function
         * @returns {string}
         * @example
         * // '/home-fr'
         * Playtech.API.menu.getRootLevel();
         */
        getRootLevel: () => rootPage,
        /**
         * Get list of configured menu items.
         * @memberOf Playtech.API.menu
         * @function
         * @returns {Object} menuItems
         * @property {string} menuItems.target
         * @property {string} menuItems.title
         * @property {string} menuItems.url
         * @property {menuItems[]} menuItems.children
         * @example
         * // {menuItems: [{target: "", title: "", url: "/home", children: [{target: "", title: "", url: "/home", children: [{...}], ...}], ...}]
         * Playtech.API.menu.getConfig();
         */
        getConfig: () => ({ menuItems }),
        /**
         * Get list of configured menu items asynchronously.
         * @memberOf Playtech.API.menu
         * @function
         * @param {boolean} [disableCache] if true data will always be taken from server instead of storage
         * @returns {Promise<{menuItems: Object[]}>}
         * @example
         * Playtech.API.menu.getMenuConfigAsync(true).then((data) => { ... });
         */
        getMenuConfigAsync,
        /**
         * Open slide menu.
         * @memberOf Playtech.API.menu
         * @function
         * @example
         * Playtech.API.menu.openSlideMenu();
         */
        openSlideMenu: () => dispatcher.dispatch('menu:action:open'),
        /**
         * Close slide menu.
         * @memberOf Playtech.API.menu
         * @function
         * @example
         * Playtech.API.menu.closeSlideMenu();
         */
        closeSlideMenu: () => dispatcher.dispatch('menu:action:close')
    }), [getMenuConfigAsync, rootPage, menuItems]);
};
